import React, { useState, useEffect } from "react";
import { useFinancialPlanState } from "../FinancialPlanContext";
import "./ContractSectionsTab.css";

const ContractSectionsTab = ({ isEditable = true }) => {
  const {
    financialPlanData,
    updateContractSections,
  } = useFinancialPlanState();

  const [contractSections, setContractSections] = useState([]);
  const [editingSections, setEditingSections] = useState({});

  const normalizeSection = (section) => {
    if (section.section) return section;
    if (section.id && section.name) {
      return {
        section: { id: section.id, name: section.name },
        amount: section.amount || 0,
      };
    }
    if (section.section_id) {
      return {
        section: { id: section.section_id, name: section.name || "" },
        amount: section.amount || 0,
      };
    }
    return section;
  };

  useEffect(() => {
    const raw = financialPlanData?.rawApiData;
    if (!raw) return;
    let sectionsArray = [];
    if (raw.contract_sections) {
      if (
        raw.contract_sections.sections &&
        Array.isArray(raw.contract_sections.sections)
      ) {
        sectionsArray = raw.contract_sections.sections;
      } else if (Array.isArray(raw.contract_sections)) {
        sectionsArray = raw.contract_sections;
      }
    }
    setContractSections(sectionsArray.map(normalizeSection));
  }, [financialPlanData?.rawApiData]);

  const getSectionId = (section) =>
    section.section?.id ?? section.id;
  const getSectionName = (section) =>
    section.section?.name ?? section.name ?? "";

  const calculateTotal = () =>
    contractSections.reduce((sum, section) => sum + (parseFloat(section.amount) || 0), 0);

  const startEditAmount = (sectionId) => {
    if (!isEditable) return;
    setEditingSections((prev) => ({ ...prev, [sectionId]: true }));
  };

  const saveAmount = (sectionId, newAmount) => {
    const amount = parseFloat(String(newAmount).replace(/\s/g, "").replace(",", ".")) || 0;
    const updatedSections = contractSections.map((section) => {
      const currentId = getSectionId(section);
      const normalized = normalizeSection(section);
      return currentId === sectionId
        ? { ...normalized, amount }
        : normalized;
    });
    setContractSections(updatedSections);
    setEditingSections((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    updateContractSections(updatedSections);
  };

  const cancelEditAmount = (sectionId) => {
    setEditingSections((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  };

  const totalAmount = calculateTotal();

  return (
    <div className="contract-sections-tab contract-sections-tab-fp">
      <div className="contract-sections-table">
        <table>
          <thead>
            <tr>
              <th>Раздел</th>
              <th style={{ textAlign: "right", width: "180px" }}>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {contractSections.length === 0 ? (
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  Нет разделов на договоре.
                </td>
              </tr>
            ) : (
              <>
                {contractSections.map((section) => {
                  const sectionId = getSectionId(section);
                  const sectionName = getSectionName(section);
                  const isEditing = editingSections[sectionId];
                  const amount = section.amount ?? 0;
                  return (
                    <tr
                      key={sectionId}
                      style={{ borderBottom: "1px solid #e9ecef" }}
                    >
                      <td
                        style={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        {sectionName}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          textAlign: "right",
                        }}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            defaultValue={amount}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                saveAmount(sectionId, e.target.value);
                              } else if (e.key === "Escape") {
                                cancelEditAmount(sectionId);
                              }
                            }}
                            autoFocus
                            style={{
                              width: "140px",
                              padding: "6px 10px",
                              border: "1.5px solid #B8C2D1",
                              borderRadius: "6px",
                              background: "#fff",
                              fontSize: "14px",
                              textAlign: "right",
                              outline: "none",
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = "#007bff";
                              const input = e.target;
                              requestAnimationFrame(() => input.select());
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = "#B8C2D1";
                              saveAmount(sectionId, e.target.value);
                            }}
                            onClick={(e) => {
                              const input = e.target;
                              requestAnimationFrame(() => input.select());
                            }}
                            disabled={!isEditable}
                          />
                        ) : (
                          <span
                            role="button"
                            tabIndex={0}
                            style={{
                              fontSize: "14px",
                              display: "inline-block",
                              minWidth: "100px",
                              padding: "6px 10px",
                              border: "1.5px dashed #B8C2D1",
                              borderRadius: "6px",
                              background: !isEditable ? "#F4F6FA" : "#F9FBFF",
                              textAlign: "right",
                              cursor: !isEditable ? "default" : "pointer",
                            }}
                            onClick={() => startEditAmount(sectionId)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                startEditAmount(sectionId);
                              }
                            }}
                          >
                            ₸{" "}
                            {typeof amount === "number"
                              ? Number(amount).toLocaleString("ru-RU", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })
                              : parseFloat(amount || 0).toLocaleString("ru-RU")}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr
                  style={{
                    borderTop: "2px solid #e9ecef",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <td
                    style={{
                      padding: "12px 16px",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#333",
                    }}
                  >
                    Итого
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      textAlign: "right",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#333",
                    }}
                  >
                    ₸{" "}
                    {totalAmount.toLocaleString("ru-RU", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContractSectionsTab;
